import {Component, NgZone, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {
    ARReportCustomerDto,
    ARReportCustomerExportSearchDto,
    ARReportCustomerSearchDto,
    ARReportCustomerSearchResultDto,
    PerFormaAPIService
} from "src/app/services/pfapi.service";
import {lastValueFrom} from "rxjs";
import {NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import Utils from "src/utils";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-customers",
    templateUrl: "./customers.component.html",
    styleUrl: "./customers.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomersComponent {
    constructor(
        private readonly api: PerFormaAPIService,
        private message: NzMessageService,
        private zone: NgZone
    ) {}

    customers: ARReportCustomerSearchResultDto = {};
    loading = true;
    total = 0;
    pageSizeOptions = [10, 15, 20, 50];
    pageSize = 15;
    pageIndex = 1;

    private lastParams: NzTableQueryParams | null = null;

    // start out with a default sort order so
    // that the column header shows the correct state
    defaultSort: SortDto[] = [
        {key: "CustomerName", value: "ascend"},
        {key: "CustomerNumber", value: null},
        {key: "EmailAddress", value: null},
        {key: "ShouldPrint", value: null},
        {key: "ShouldEmail", value: null},
        {key: "EditCustomer", value: null}
    ];

    sort: SortDto[] = [...this.defaultSort];

    searchField = "CustomerNumber";
    searchValue = "";
    searchFields = [
        {label: "Customer Number", value: "CustomerNumber"},
        {label: "Customer Name", value: "CustomerName"},
        {label: "Customer Email", value: "EmailAddress"}
    ];

    columns = [
        {
            title: "Customer Name",
            compare: (a: ARReportCustomerDto, b: ARReportCustomerDto) => a.CustomerName.localeCompare(b.CustomerName),
            fieldName: "CustomerName"
        },
        {
            title: "Customer Number",
            compare: (a: ARReportCustomerDto, b: ARReportCustomerDto) =>
                a.CustomerNumber.localeCompare(b.CustomerNumber),
            fieldName: "CustomerNumber"
        },
        {
            title: "Customer Email",
            compare: (a: ARReportCustomerDto, b: ARReportCustomerDto) => a.EmailAddress.localeCompare(b.EmailAddress),
            fieldName: "EmailAddress"
        },
        {
            title: "Print",
            compare: (a: ARReportCustomerDto, b: ARReportCustomerDto) =>
                a.ShouldPrint === b.ShouldPrint ? 0 : a.ShouldPrint ? -1 : 1,
            fieldName: "ShouldPrint"
        },
        {
            title: "Email",
            compare: (a: ARReportCustomerDto, b: ARReportCustomerDto) =>
                a.ShouldEmail === b.ShouldEmail ? 0 : a.ShouldEmail ? -1 : 1,
            fieldName: "ShouldEmail"
        },
        {
            title: "Edit Customer",
            compare: null,
            fieldName: null
        }
    ];

    GetData = async () => {
        const params: ARReportCustomerSearchDto = {
            PageIndex: this.pageIndex,
            PageSize: this.pageSize,
            SearchField: this.searchField || null,
            SearchValue: this.searchValue || null
        };

        const sort = this.sort?.filter(s => s.value !== null) || [...this.defaultSort];

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value;
        }

        try {
            this.customers = await lastValueFrom(this.api.postApiReportArCustomersSearch(params));
            this.total = this.customers.Total;
        } catch (error) {
            this.message.error("Failed to fetch data");
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    ExportData = async (all: boolean) => {
        this.loading = true;

        const params: ARReportCustomerExportSearchDto = {};

        if (!all) {
            params.SearchField = this.searchField;
            params.SearchValue = this.searchValue;
        }

        const sort = this.sort?.filter(s => s.value !== null) || [...this.defaultSort];

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value;
        }

        try {
            const blob = await lastValueFrom(
                this.api.postApiReportArCustomersExport(params, {
                    observe: "response"
                })
            );

            Utils.downloadFileContent(blob, true);
        } catch (e) {
            this.message.error("something went wrong");
            console.log(e);
        } finally {
            this.loading = false;
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};

        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        // reset the page index if the sort changes
        if (this.SortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        this.sort = [...params.sort];
        await this.GetData();
    }

    // nz-table likes to fire extraneous onQueryParamsChange events
    // so we need to filter out the ones that don't actually change anything
    private isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value)
        );
    }

    SortChanged = (sort: SortDto[]): boolean => {
        // Filter out non-active sorts (where value is null)
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        // If both are empty, no sort change
        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }

        // If lengths differ, there's a change (e.g., one has a sort, the other doesn't)
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];

        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    getSortOrder(fieldName: string | null): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }

    // Handle search button click
    async onSearch(): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    // Optional: Clear search
    async clearSearch(): Promise<void> {
        this.searchField = "CustomerNumber";
        this.searchValue = "";
        this.pageIndex = 1;
        this.sort = [...this.defaultSort];
        await this.GetData();
    }

    async onExport(all?: boolean): Promise<void> {
        await this.ExportData(all);
    }
}
